/**
 * The single navigation registry (Rule 56).
 *
 * The sidebar, the mobile bottom bar and the route map all iterate THIS list, so
 * a new module cannot appear in one and be missing from another. Adding a module
 * = one entry here + a lazy import in `routes/index.jsx`.
 *
 * `label`/`group` stay in the file as English fallbacks; `labelKey`/`groupKey`
 * are what the shell actually renders through `t()`. Keeping both means a nav
 * entry added without a locale key still shows readable text instead of a raw
 * key string.
 *
 * `requiredScopes` is an OR set resolved through SCOPE_ALIAS_MAP, matching the
 * backend's RequireOauthScope. It hides what the API would refuse — it is not
 * itself the security boundary (that is the scope middleware plus the Rule-65
 * ownership predicate in the SQL).
 *
 * The tab order mirrors the AI Studio prototype (spec §7): Overview, Members,
 * Branches, Billing, Claims, Network, Tickets, Reports for admins; Benefits,
 * Billing, Claims, Network for members.
 */

import { hasAnyScope } from '../permissions/scopePermissions';

export const APP_NAV_ITEMS = [
  // ── Shared landing ────────────────────────────────────────────────────────
  {
    path: '/dashboard',
    label: 'Overview',
    labelKey: 'nav.overview',
    iconKey: 'dashboard',
    requiredScopes: ['dashboard.view'],
  },

  // ── Member portal ─────────────────────────────────────────────────────────
  // A member's four tabs, FLAT — the prototype's sidebar is one unbroken list,
  // and a four-item list gains nothing from a collapsible header. Staff hold
  // none of these member-only scopes, so none of it renders for them.
  {
    path: '/portal/profile',
    label: 'My Profile',
    labelKey: 'nav.myProfile',
    iconKey: 'idCard',
    requiredScopes: ['members.update-own'],
  },
  {
    path: '/portal/benefits',
    label: 'Benefits Catalog',
    labelKey: 'nav.benefits',
    iconKey: 'shield',
    requiredScopes: ['claims.file', 'benefit-catalog.view'],
  },
  {
    path: '/portal/billing',
    label: 'My Billing',
    labelKey: 'nav.myBilling',
    iconKey: 'receipt',
    requiredScopes: ['billing.submit-proof'],
  },
  {
    path: '/portal/claims',
    label: 'My Claims',
    labelKey: 'nav.myClaims',
    iconKey: 'lifebuoy',
    requiredScopes: ['claims.file'],
  },
  {
    path: '/portal/network',
    label: 'My Network',
    labelKey: 'nav.myNetwork',
    iconKey: 'network',
    requiredScopes: ['network.view'],
  },

  // ── RMC2 core (spec §7 sidebar order) ─────────────────────────────────────
  // Overview · Members · Branch Offices · Billing · Claims · Network ·
  // Support Inbox · Reports & Analytics — flat, in this order, exactly as the
  // prototype presents them. The platform modules below KEEP their group
  // headers: they are a different job (running the software, not the
  // business) and only a super admin ever sees them.
  {
    path: '/members',
    label: 'Members',
    labelKey: 'nav.members',
    iconKey: 'users',
    requiredScopes: ['members.view', 'members.view-all'],
  },
  {
    path: '/branches',
    label: 'Branch Offices',
    labelKey: 'nav.branches',
    iconKey: 'building',
    requiredScopes: ['branches.view'],
  },
  {
    path: '/billing',
    label: 'Billing',
    labelKey: 'nav.billingLedger',
    iconKey: 'receipt',
    requiredScopes: ['billing.view-all', 'billing.verify'],
  },
  {
    path: '/claims',
    label: 'Claims',
    labelKey: 'nav.claimsDesk',
    iconKey: 'lifebuoy',
    requiredScopes: ['claims.view-all', 'claims.review'],
  },
  {
    path: '/overrides',
    label: 'House Overrides',
    labelKey: 'nav.houseOverrides',
    iconKey: 'wallet',
    requiredScopes: ['overrides.view'],
  },
  {
    path: '/network',
    label: 'Network',
    labelKey: 'nav.networkPayouts',
    iconKey: 'network',
    requiredScopes: ['network.view-all', 'payouts.view-all'],
  },
  {
    path: '/support',
    label: 'Support Inbox',
    labelKey: 'nav.helpdesk',
    iconKey: 'chat',
    requiredScopes: ['support.view', 'support.view-all'],
  },
  {
    path: '/reports',
    label: 'Reports & Analytics',
    labelKey: 'nav.reports',
    iconKey: 'chart',
    requiredScopes: ['reports.view'],
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  // Bank Holidays (Rule 74) and the Benefit Catalog (Rule 76) are admin-editable
  // ROWS by design, and both tables + their scopes exist — but neither has an
  // admin controller or route yet, so there is nothing for a nav entry to open.
  // They are deliberately absent rather than present-and-404ing; add the entry in
  // the same change that adds the endpoint.

  // ── Platform administration (inherited) ───────────────────────────────────
  {
    path: '/admin/users',
    label: 'Users',
    labelKey: 'nav.users',
    iconKey: 'users',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['oauth-users.view'],
  },
  {
    path: '/admin/oauth-clients',
    label: 'OAuth Clients',
    labelKey: 'nav.oauthClients',
    iconKey: 'key',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['oauth-clients.view'],
  },
  {
    // Rule 74 — these rows decide every payout's promised credit date, so the
    // calendar is an operator surface, not a constant in the code.
    path: '/settings/bank-holidays',
    label: 'Bank Holidays',
    labelKey: 'nav.bankHolidays',
    iconKey: 'calendar',
    group: 'Settings',
    groupKey: 'nav.group.settings',
    groupIconKey: 'settings',
    requiredScopes: ['bank-holidays.view'],
  },
  {
    // Rule 76 — the ceilings every claim decision is bounded by.
    path: '/settings/benefit-catalog',
    label: 'Benefit Catalog',
    labelKey: 'nav.benefitCatalog',
    iconKey: 'shield',
    group: 'Settings',
    groupKey: 'nav.group.settings',
    groupIconKey: 'settings',
    requiredScopes: ['benefit-catalog.view'],
  },
  {
    path: '/admin/system-settings',
    label: 'System Settings',
    labelKey: 'nav.systemSettings',
    iconKey: 'settings',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['system-settings.view'],
  },
  {
    path: '/admin/audit-logs',
    label: 'Audit Logs',
    labelKey: 'nav.auditLogs',
    iconKey: 'list',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['admin-audit-logs.view'],
  },
  {
    path: '/admin/field-history',
    label: 'Field History',
    labelKey: 'nav.fieldHistory',
    iconKey: 'list',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['admin-field-history.view'],
  },
  {
    path: '/admin/login-attempts',
    label: 'Login Attempts',
    labelKey: 'nav.loginAttempts',
    iconKey: 'list',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['admin-login-attempts.view'],
  },
  {
    path: '/admin/outbound-http-logs',
    label: 'Outbound HTTP',
    labelKey: 'nav.outboundHttp',
    iconKey: 'list',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['admin-outbound-http-logs.view'],
  },
  {
    path: '/admin/failed-jobs',
    label: 'Failed Jobs',
    labelKey: 'nav.failedJobs',
    iconKey: 'list',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['admin-failed-jobs.view'],
  },
  {
    path: '/admin/upload-s3-public-share-tokens',
    label: 'Share Tokens',
    labelKey: 'nav.shareTokens',
    iconKey: 'key',
    group: 'System Administrator',
    groupKey: 'nav.group.systemAdministrator',
    groupIconKey: 'shieldCheck',
    requiredScopes: ['upload-s3-public-share-token.view'],
  },
];

/**
 * The mobile bottom bar (<768px), which is role-aware.
 *
 * A member's day is Benefits / Billing / Network / Claims; an admin's is the
 * verification queue. Showing an admin "My Benefits" or a member "Members" would
 * waste the four slots people actually reach for.
 */
export function getPrimaryNavItems(hasScope) {
  const isAdmin = hasScope('billing.verify') || hasScope('claims.review') || hasScope('members.view-all');

  const paths = isAdmin
    ? ['/dashboard', '/members', '/billing', '/claims']
    : ['/portal/benefits', '/portal/billing', '/portal/network', '/portal/claims'];

  return paths
    .map((p) => APP_NAV_ITEMS.find((item) => item.path === p))
    .filter(Boolean);
}

export function canAccessNavItem(userScopes, navItem) {
  const normalized = Array.isArray(userScopes) ? userScopes : [];
  if (navItem?.path === '/dashboard' && normalized.length === 0) return true;
  return hasAnyScope(normalized, navItem?.requiredScopes ?? []);
}

/**
 * Flat list of accessible items (used for the fallback-route redirect).
 * Falls back to dashboard if nothing matches.
 */
export function getAccessibleNavItems(userScopes) {
  const items = APP_NAV_ITEMS.filter((item) => canAccessNavItem(userScopes, item));
  return items.length > 0 ? items : APP_NAV_ITEMS.filter((i) => i.path === '/dashboard');
}

/**
 * Sidebar structure: an ordered list of entries that are either
 *   { type: 'item',  item }
 *   { type: 'group', label, iconKey, children: [item, ...] }
 *
 * Groups appear at the position of their first accessible child. A group
 * with no accessible children is omitted entirely.
 */
export function getAccessibleNavGroups(userScopes) {
  const accessible = getAccessibleNavItems(userScopes);
  const entries = [];
  const groupIndex = new Map();

  accessible.forEach((item) => {
    if (!item.group) {
      entries.push({ type: 'item', item });
      return;
    }
    if (!groupIndex.has(item.group)) {
      const group = {
        type:     'group',
        label:    item.group,
        labelKey: item.groupKey,
        iconKey:  item.groupIconKey ?? 'folder',
        children: [],
      };
      groupIndex.set(item.group, group);
      entries.push(group);
    }
    groupIndex.get(item.group).children.push(item);
  });

  return entries;
}

export default APP_NAV_ITEMS;
